import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { DemoPopup } from '../components/forms/DemoPopup';
import { Target, Rocket, Heart, Users, Building2, Globe, Calendar } from 'lucide-react';

const stats = [
  { value: '2019', label: 'Founded', icon: Calendar },
  { value: '50+', label: 'Team Members', icon: Users },
  { value: '500+', label: 'Happy Clients', icon: Building2 },
  { value: '15+', label: 'Countries', icon: Globe },
];

const values = [
  {
    icon: Target,
    title: 'Our Vision',
    description: 'To become the world\'s most trusted business management platform, enabling organizations of all sizes to operate at peak efficiency.',
  },
  {
    icon: Rocket,
    title: 'Our Mission',
    description: 'To simplify business operations through innovative technology, making powerful tools accessible and affordable for every organization.',
  },
  {
    icon: Heart,
    title: 'Our Values',
    description: 'Innovation, integrity, customer-centricity, and continuous improvement guide everything we do. We succeed when our clients succeed.',
  },
];

export const AboutPage = () => {
  return (
    <div className="min-h-screen pt-20" data-testid="about-page">
      {/* Hero Banner */}
      <section className="py-20 md:py-28 hero-glow" data-testid="about-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-block px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6 animate-fade-in-up">
            About Niyanta.io
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold font-heading text-white animate-fade-in-up stagger-1">
            Empowering Businesses Worldwide
          </h1>
          <p className="text-lg md:text-xl text-muted-foreground mt-6 max-w-3xl mx-auto animate-fade-in-up stagger-2">
            With intelligent solutions that drive growth and operational excellence.
          </p>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-24 md:py-32" data-testid="about-story">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8 animate-fade-in-up">
              <div>
                <span className="text-primary text-sm font-medium uppercase tracking-widest">
                  Our Story
                </span>
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-heading text-white mt-4">
                  Building the Future of Business Management
                </h2>
              </div>
              <p className="text-muted-foreground text-lg leading-relaxed">
                Founded with a vision to simplify complex business operations, Niyanta.io has grown 
                from a small startup to a trusted partner for hundreds of businesses across industries.
              </p>
              <p className="text-muted-foreground text-lg leading-relaxed">
                Our team of passionate engineers, designers, and business experts work tirelessly to 
                deliver solutions that not only meet but exceed our clients' expectations. We believe 
                technology should empower, not complicate.
              </p>
              <p className="text-muted-foreground text-lg leading-relaxed">
                Today, we serve clients ranging from ambitious startups to established enterprises, 
                helping them streamline operations, boost productivity, and achieve sustainable growth.
              </p>
            </div>

            <div className="relative animate-fade-in-up stagger-2">
              <div className="rounded-2xl overflow-hidden border border-white/10">
                <img
                  src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&q=80"
                  alt="Our Team"
                  className="w-full h-auto"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 md:py-20 bg-secondary/30 border-y border-border" data-testid="about-stats">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div
                key={stat.label}
                className={`text-center animate-fade-in-up stagger-${index + 1}`}
                data-testid={`about-stat-${stat.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="feature-icon mx-auto mb-4">
                  <stat.icon className="w-6 h-6" />
                </div>
                <div className="text-3xl md:text-4xl font-bold text-primary font-heading">
                  {stat.value}
                </div>
                <div className="text-muted-foreground text-sm mt-2 uppercase tracking-wider">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Vision, Mission, Values */}
      <section className="py-24 md:py-32" data-testid="about-values">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <div
                key={value.title}
                className={`glass rounded-2xl p-8 card-hover animate-fade-in-up stagger-${index + 1}`}
                data-testid={`value-card-${value.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="feature-icon mb-6">
                  <value.icon className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-semibold font-heading text-white mb-4">
                  {value.title}
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Strip */}
      <section className="py-20 md:py-28 bg-secondary/30 border-y border-border" data-testid="about-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold font-heading text-white mb-6 animate-fade-in-up">
            Want to simplify your business operations?
          </h2>
          <p className="text-muted-foreground text-lg mb-8 max-w-2xl mx-auto animate-fade-in-up stagger-1">
            Join hundreds of businesses that trust Niyanta.io for their digital transformation journey.
          </p>
          <div className="animate-fade-in-up stagger-2">
            <DemoPopup 
              trigger={
                <Button
                  className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-10 py-6 text-lg font-medium glow-primary glow-primary-hover btn-shine"
                  data-testid="about-cta-btn"
                >
                  Book Demo
                </Button>
              }
            />
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
